import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useProductReadContext } from '@context/ProductContext'

import { Modal } from '@ui/v2/Modal'
import type { CommonModalProps } from '@ui/v2/Modal/types'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { dispatchOpenChat } from '@utils/cookie.utils'
import {
  WalletRestrictions,
  isDepositEnabledFromAction,
  isRestricted,
  isWalletSuspended,
} from '@utils/wallet.utils'

import { NameDto } from '@model/NameDto'
import { TickmillProductType } from '@model/TickmillProductType'
import { WalletDto, WalletTypeEnum } from '@model/WalletDto'

import { useCallbackWithForceRefresh } from '@hooks/useCallbackWithForceRefresh'
import { useFetchOne } from '@hooks/useFetch'
import { useFormatNumber } from '@hooks/useFormatNumber'

import { Button } from '../button/Button'

interface WalletRestrictionsModalProps extends CommonModalProps {
  onSelectOption?(option: WalletDto): void
  render?({ options }: { options: WalletDto[] }): React.ReactNode[]
  onConfirm?(): void
}

export const useWalletRestrictions = () => {
  const location = useLocation()
  const apiClient = useApiClient(ClientApiClient)
  const { product } = useProductReadContext()

  const { callback } = useCallbackWithForceRefresh(async () => {
    let walletTypeId = WalletTypeEnum.CFD
    if (product === TickmillProductType.ETD) {
      walletTypeId = WalletTypeEnum.ETD
    } else if (location.pathname.includes('introducing-broker')) {
      walletTypeId = WalletTypeEnum.IB
    } else if (location.pathname.includes('payment-agent')) {
      walletTypeId = WalletTypeEnum.PAYMENT_AGENT
    }
    return apiClient.getWallets({ walletTypeId })
  }, [product])
  const { data: wallets = [], isLoading } = useFetchOne(callback)

  return { wallets, isLoading }
}

export const WalletRestrictionsModal: React.FC<WalletRestrictionsModalProps> = (props) => {
  const { title = '', onClose, render, children } = props

  const { wallets, isLoading } = useWalletRestrictions()

  const options = render?.({ options: wallets }) || []

  return (
    <Modal.Radio title={title} options={options} isLoading={isLoading} onClose={onClose}>
      {options?.length <= 0 ? children : null}
    </Modal.Radio>
  )
}

interface WalletDepositOptionProps {
  label?: string
  selected: boolean
  item: WalletDto
  restrictionType?: WalletRestrictions
  onSelectOption(option: WalletDto): void
  disabled?: boolean
  hideHint?: boolean
}

export const WalletModalOption: React.FC<WalletDepositOptionProps> = (props) => {
  const { label, disabled, selected, item, restrictionType, onSelectOption, hideHint } = props

  const { t } = useTranslation()
  const location = useLocation()
  const { formatMoney } = useFormatNumber()

  const walletName = location.pathname.includes('introducing-broker')
    ? t('Wallet.IB Wallet')
    : t('Wallet.Wallet')

  return (
    <Modal.Radio.Option
      label={label || `${item.currency?.id} ${walletName}`}
      hint={
        !hideHint ? `${t('Wallet.Balance')} ${formatMoney(item.balance, item.currency.id)}` : ''
      }
      checked={selected}
      disabled={disabled}
      onClick={() => onSelectOption(item)}
    >
      <WalletRestriction restrictions={item.restrictions} restrictionType={restrictionType} />
    </Modal.Radio.Option>
  )
}

export const isWalletRestrictionsAllowed = (
  item: WalletDto,
  restrictionType: WalletRestrictions
) => {
  return (
    !(isRestricted(restrictionType, item.restrictions) || isWalletSuspended(item.restrictions)) &&
    ((restrictionType === WalletRestrictions.DEPOSIT &&
      isDepositEnabledFromAction(item.depositAction)) ||
      restrictionType !== WalletRestrictions.DEPOSIT)
  )
}

interface WalletRestrictionProps {
  restrictions: NameDto[] | undefined
  restrictionType?: WalletRestrictions
}

export const WalletRestriction: React.FC<WalletRestrictionProps> = (props) => {
  const { restrictions, restrictionType } = props

  const { t } = useTranslation()

  if (isWalletSuspended(restrictions)) {
    return (
      <div>
        <div className='has-error'>{t('Transfers restricted for this TM Wallet')}</div>
        <div>
          {t('Please')}{' '}
          <Button onClick={dispatchOpenChat} appearance='link'>
            {t('Contact Support').toLowerCase()}
          </Button>{' '}
          {t('for more info.')}
        </div>
      </div>
    )
  }

  if (restrictionType && isRestricted(restrictionType, restrictions)) {
    return (
      <div>
        <div className='has-error'>{t('Temporarily restricted!')}</div>
        <div>
          {t('Please')}{' '}
          <Button onClick={dispatchOpenChat} appearance='link'>
            {t('Contact Support').toLowerCase()}
          </Button>{' '}
          {t('for more info.')}
        </div>
      </div>
    )
  }

  return null
}
